import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useTheme } from '../../context/ThemeContext';

// Postal code regex patterns by country
const POSTAL_PATTERNS = {
  US: /^\d{5}(-\d{4})?$/,
  CA: /^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/,
  GB: /^[A-Za-z]{1,2}\d[A-Za-z\d]? \d[A-Za-z]{2}$/,
  DE: /^\d{5}$/,
  FR: /^\d{5}$/,
  AU: /^\d{4}$/,
  NL: /^\d{4} [A-Za-z]{2}$/,
  CH: /^\d{4}$/,
  IT: /^\d{5}$/,
  ES: /^\d{5}$/,
  JP: /^\d{3}-\d{4}$/,
  BR: /^\d{5}-\d{3}$/,
  default: /^[A-Za-z0-9\s-]{3,10}$/
};

// Email regex pattern
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Phone number patterns
const PHONE_PATTERNS = {
  US: /^\(\d{3}\) \d{3}-\d{4}$/,
  international: /^\+\d{1,15}$/,
  digits: /^\d{10,15}$/
};

// Common validation patterns
export const VALIDATION_PATTERNS = {
  email: EMAIL_PATTERN,
  postalCode: (countryCode) => POSTAL_PATTERNS[countryCode] || POSTAL_PATTERNS.default,
  usZip: POSTAL_PATTERNS.US,
  canadaPostal: POSTAL_PATTERNS.CA,
  ukPostal: POSTAL_PATTERNS.GB,
  phone: PHONE_PATTERNS.digits,
  usPhone: PHONE_PATTERNS.US,
  internationalPhone: PHONE_PATTERNS.international,
  lettersOnly: /^[A-Za-z\s'-]+$/,
  numbersOnly: /^\d+$/,
  alphanumeric: /^[A-Za-z0-9]+$/,
  noSpecialChars: /^[A-Za-z0-9\s]+$/
};

const TextField = ({
  label,
  id,
  type = 'text',
  placeholder,
  value = '',
  onChange,
  onBlur,
  onSaveOnBlur,
  required = false,
  disabled = false,
  className = '',
  containerClassName = '',
  error,
  validateOnBlur = true,
  validateOnChange = false,
  requiredMessage = 'This field is required',
  regexPattern,
  regexMessage = 'Invalid format',
  validationType,
  countryCode = 'US',
  ...props
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  // Internal state for validation errors
  const [validationError, setValidationError] = useState('');
  const [regexError, setRegexError] = useState('');
  const [hasBlurred, setHasBlurred] = useState(false);
  const [initialValue, setInitialValue] = useState(value);

  // Get the pattern to use for validation
  const getValidationPattern = useCallback(() => {
    if (regexPattern) {
      return regexPattern;
    }
    
    if (validationType) {
      if (validationType === 'postalCode') {
        return VALIDATION_PATTERNS.postalCode(countryCode);
      }
      return VALIDATION_PATTERNS[validationType];
    }
    
    return null;
  }, [regexPattern, validationType, countryCode]);

  const activePattern = useMemo(() => getValidationPattern(), [getValidationPattern]);

  // Validate against regex pattern
  const validateRegex = useCallback((inputValue) => {
    if (activePattern && inputValue.trim()) {
      if (!activePattern.test(inputValue)) {
        setRegexError(regexMessage);
        return false;
      } else {
        setRegexError('');
        return true;
      }
    }
    return true;
  }, [activePattern, regexMessage]);

  // Determine which error to show (external error takes precedence)
  const displayError = error || (hasBlurred && (validationError || regexError));

  const handleBlur = useCallback((e) => {
    setHasBlurred(true);
    const fieldValue = e.target.value.trim();

    // Validate if field is required and empty
    if (validateOnBlur && required) {
      if (!fieldValue) {
        setValidationError(requiredMessage);
        return
      } else {
        setValidationError('');
      }
    }
    
    // Validate regex pattern on blur
    if (validateOnBlur && fieldValue) {
      validateRegex(fieldValue);
    }
    
    // Handle save on blur if value has changed
    if (onSaveOnBlur && fieldValue !== initialValue) {
      onSaveOnBlur(e);
      setInitialValue(fieldValue);
    }
    
    // Call external onBlur handler if provided
    if (onBlur) {
      onBlur(e);
    }
  }, [onBlur, validateOnBlur, required, requiredMessage, initialValue, onSaveOnBlur]);

  const handleChange = useCallback((e) => {
    const inputValue = e.target.value;
    // console.log('Field changed to:', inputValue);
    // Clear validation error when user starts typing after blur
    if (hasBlurred && validationError && inputValue.trim()) {
      setValidationError('');
    }
    
    // Real-time regex validation if enabled
    if (validateOnChange && activePattern && inputValue) {
      validateRegex(inputValue);
    } else if (validateOnChange && !inputValue) {
      // Clear regex error when field is empty
      setRegexError('');
    }
    
    // Call external onChange handler
    onChange(e);
  }, [onChange, validateOnChange, activePattern, validateRegex]);

  // Clear validation error when external error is provided
  React.useEffect(() => {
    if (error) {
      setValidationError('');
      setRegexError('');
    }
  }, [error]);

  // Update initial value when value prop changes
  // React.useEffect(() => {
  //   setInitialValue(value);
  // }, [value]);

  const labelClassName = clsx(
    'block text-sm font-normal mb-2',
    {
      'text-slate-200': isDark,
      'text-gray-700': !isDark,
      'text-red-400': displayError && isDark,
      'text-red-600': displayError && !isDark
    }
  );

  const inputClassName = clsx(
    'w-full text-sm px-3 py-3 border rounded-lg transition-colors',
    'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
    {
      'bg-slate-800 border-slate-600 text-slate-200 placeholder-slate-400': isDark && !displayError,
      'bg-white border-gray-300 text-gray-900 placeholder-gray-400': !isDark && !displayError,
      'border-red-500 bg-red-50': displayError && !isDark,
      'border-red-400 bg-slate-800': displayError && isDark,
      'opacity-50 cursor-not-allowed': disabled
    },
    className
  );

  const containerClasses = clsx('relative', containerClassName);

  const errorClassName = clsx(
    'text-xs mt-1',
    {
      'text-red-400': isDark,
      'text-red-600': !isDark
    }
  );

  return (
    <div className={containerClasses}>
      {label && (
        <label htmlFor={id} className={labelClassName}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        disabled={disabled}
        className={inputClassName}
        aria-invalid={displayError ? 'true' : 'false'}
        aria-describedby={displayError ? `${id}-error` : undefined}
        {...props}
      />
      {displayError && (
        <div id={`${id}-error`} className={errorClassName}>
          {displayError}
        </div>
      )}
    </div>
  );
};

TextField.propTypes = {
  label: PropTypes.string,
  id: PropTypes.string.isRequired,
  type: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  onSaveOnBlur: PropTypes.func,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  containerClassName: PropTypes.string,
  error: PropTypes.string,
  validateOnBlur: PropTypes.bool,
  requiredMessage: PropTypes.string,
  regexPattern: PropTypes.instanceOf(RegExp),
  regexMessage: PropTypes.string,
  validateOnChange: PropTypes.bool,
  validationType: PropTypes.oneOf([
    'email', 'usZip', 'postalCode', 'phone', 'usPhone', 'internationalPhone',
    'lettersOnly', 'numbersOnly', 'alphanumeric', 'noSpecialChars'
  ]),
  countryCode: PropTypes.string
};

export default TextField;